import React, { useEffect, useRef, useState } from "react";
import "./Spinner.css";

interface Props {
  visible: boolean;
  label?: string;
}

/**
 * Spinner with built-in timing:
 * - Waits 300ms after visible=true before rendering (avoids flash on fast loads)
 * - Once shown, stays visible for at least 500ms (avoids flicker)
 * - Sibling elements are automatically dimmed via CSS (.spinner ~ *)
 */
const Spinner: React.FC<Props> = ({ visible, label }) => {
  const [render, setRender] = useState(false);
  const shownAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (visible) {
      timerRef.current = setTimeout(() => {
        shownAtRef.current = Date.now();
        setRender(true);
      }, 300);
    } else {
      const elapsed = Date.now() - shownAtRef.current;
      const remaining = shownAtRef.current > 0 ? 500 - elapsed : 0;
      if (remaining > 0) {
        timerRef.current = setTimeout(() => {
          shownAtRef.current = 0;
          setRender(false);
        }, remaining);
      } else {
        shownAtRef.current = 0;
        setRender(false);
      }
    }

    return () => clearTimeout(timerRef.current);
  }, [visible]);

  if (!render) return null;

  return (
    <div className="spinner">
      <div className="spinner-dot" />
      {label && <span>{label}</span>}
    </div>
  );
};

export default Spinner;
